import React, { useState, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, FileText, Eye, EyeOff } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface SteganographyStats {
  totalOperations: number;
  messagesHidden: number;
  messagesExtracted: number;
  totalCharactersHidden: number;
  averageMessageLength: number;
  methodsUsed: Record<string, number>;
}

interface SteganographyHistory {
  id: string;
  operation: 'hide' | 'extract';
  method: string;
  timestamp: Date;
  success: boolean;
}

type SteganographyMethod = 'whitespace' | 'invisible' | 'unicode' | 'acrostic';

const TextSteganographyApp: React.FC = () => {
  // Core state
  const [coverText, setCoverText] = useState<string>('');
  const [secretMessage, setSecretMessage] = useState<string>('');
  const [steganographicText, setSteganographicText] = useState<string>('');
  const [extractedMessage, setExtractedMessage] = useState<string>('');
  const [operation, setOperation] = useState<'hide' | 'extract'>('hide');
  const [method, setMethod] = useState<SteganographyMethod>('whitespace');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [showInvisibleChars, setShowInvisibleChars] = useState<boolean>(false);
  
  // Advanced options
  const [password, setPassword] = useState<string>('');
  const [useEncryption, setUseEncryption] = useState<boolean>(false);
  
  // Analytics state
  const [stats, setStats] = useState<SteganographyStats>({
    totalOperations: 0,
    messagesHidden: 0,
    messagesExtracted: 0,
    totalCharactersHidden: 0,
    averageMessageLength: 0,
    methodsUsed: {}
  });
  const [history, setHistory] = useState<SteganographyHistory[]>([]);

  // Simple XOR encryption
  const encryptText = useCallback((text: string, key: string): string => {
    if (!key) return text;
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return btoa(result);
  }, []);

  const decryptText = useCallback((encrypted: string, key: string): string => {
    if (!key) return encrypted;
    try {
      const decoded = atob(encrypted);
      let result = '';
      for (let i = 0; i < decoded.length; i++) {
        result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
      }
      return result;
    } catch {
      return '';
    }
  }, []);

  // Whitespace steganography
  const hideInWhitespace = useCallback((cover: string, message: string): string => {
    const processedMessage = useEncryption ? encryptText(message, password) : message;
    
    // Convert message to binary
    const binary = processedMessage.split('').map(char => 
      char.charCodeAt(0).toString(2).padStart(8, '0')
    ).join('');
    
    const words = cover.split(' ');
    if (words.length - 1 < binary.length) {
      throw new Error('Cover text too short for message. Need more words.');
    }
    
    let result = words[0] || '';
    
    for (let i = 1; i < words.length && i - 1 < binary.length; i++) {
      const bit = binary[i - 1];
      const spaces = bit === '0' ? ' ' : '  '; // Single space for 0, double for 1
      result += spaces + words[i];
    }
    
    // Add remaining words with single spaces
    for (let i = binary.length + 1; i < words.length; i++) {
      result += ' ' + words[i];
    }
    
    return result;
  }, [useEncryption, encryptText, password]);

  const extractFromWhitespace = useCallback((text: string): string => {
    const parts = text.split(/(\s+)/);
    let binary = '';
    
    for (let i = 1; i < parts.length; i += 2) {
      const spaces = parts[i];
      if (spaces === ' ') {
        binary += '0';
      } else if (spaces === '  ') {
        binary += '1';
      } else if (spaces.length > 2) {
        binary += '1'; // Multiple spaces treated as 1
      }
    }
    
    // Convert binary to text
    let result = '';
    for (let i = 0; i < binary.length; i += 8) {
      const byte = binary.slice(i, i + 8);
      if (byte.length === 8) {
        const charCode = parseInt(byte, 2);
        if (charCode > 0 && charCode < 127) { // Valid ASCII range
          result += String.fromCharCode(charCode);
        }
      }
    }
    
    // Clean up result
    result = result.replace(/\0+$/, '').trim();
    
    return useEncryption ? decryptText(result, password) : result;
  }, [useEncryption, decryptText, password]);

  // Invisible character steganography
  const hideInInvisible = useCallback((cover: string, message: string): string => {
    const processedMessage = useEncryption ? encryptText(message, password) : message;
    
    const binary = processedMessage.split('').map(char => 
      char.charCodeAt(0).toString(2).padStart(8, '0')
    ).join('');
    
    const invisibleChars = ['\u200B', '\u200C']; // Zero width space, Zero width non-joiner
    let result = '';
    let binaryIndex = 0;
    
    for (let i = 0; i < cover.length; i++) {
      result += cover[i];
      
      // Insert invisible character after spaces and punctuation
      if ((cover[i] === ' ' || cover[i] === '.' || cover[i] === ',') && binaryIndex < binary.length) {
        const bit = binary[binaryIndex];
        result += invisibleChars[parseInt(bit)];
        binaryIndex++;
      }
    }
    
    return result;
  }, [useEncryption, encryptText, password]);

  const extractFromInvisible = useCallback((text: string): string => {
    const invisibleChars = ['\u200B', '\u200C'];
    let binary = '';
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const index = invisibleChars.indexOf(char);
      if (index !== -1) {
        binary += index.toString();
      }
    }
    
    let result = '';
    for (let i = 0; i < binary.length; i += 8) {
      const byte = binary.slice(i, i + 8);
      if (byte.length === 8) {
        const charCode = parseInt(byte, 2);
        if (charCode > 0 && charCode < 127) {
          result += String.fromCharCode(charCode);
        }
      }
    }
    
    result = result.replace(/\0+$/, '').trim();
    return useEncryption ? decryptText(result, password) : result;
  }, [useEncryption, decryptText, password]);

  // Unicode steganography
  const hideInUnicode = useCallback((cover: string, message: string): string => {
    const processedMessage = useEncryption ? encryptText(message, password) : message;
    
    const binary = processedMessage.split('').map(char => 
      char.charCodeAt(0).toString(2).padStart(8, '0')
    ).join('');
    
    const replacements: Record<string, string> = {
      'a': 'а', 'e': 'е', 'o': 'о', 'p': 'р', 'c': 'с' // Cyrillic look-alikes
    };
    
    let result = '';
    let binaryIndex = 0;
    
    for (let i = 0; i < cover.length && binaryIndex < binary.length; i++) {
      const char = cover[i].toLowerCase();
      if (replacements[char]) {
        const bit = binary[binaryIndex];
        result += bit === '1' ? replacements[char] : cover[i];
        binaryIndex++;
      } else {
        result += cover[i];
      }
    }
    
    result += cover.slice(result.length);
    return result;
  }, [useEncryption, encryptText, password]);

  const extractFromUnicode = useCallback((text: string): string => {
    const cyrillicChars = ['а', 'е', 'о', 'р', 'с'];
    const latinChars = ['a', 'e', 'o', 'p', 'c'];
    let binary = '';
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (cyrillicChars.includes(char)) {
        binary += '1';
      } else if (latinChars.includes(char.toLowerCase())) {
        binary += '0';
      }
    }
    
    let result = '';
    for (let i = 0; i < binary.length; i += 8) {
      const byte = binary.slice(i, i + 8);
      if (byte.length === 8) {
        const charCode = parseInt(byte, 2);
        if (charCode > 0 && charCode < 127) {
          result += String.fromCharCode(charCode);
        }
      }
    }
    
    result = result.replace(/\0+$/, '').trim();
    return useEncryption ? decryptText(result, password) : result;
  }, [useEncryption, decryptText, password]);

  // Acrostic steganography
  const hideInAcrostic = useCallback((cover: string, message: string): string => {
    const processedMessage = (useEncryption ? encryptText(message, password) : message).toUpperCase();
    const lines = cover.split('\n');
    const result = [];
    
    for (let i = 0; i < Math.max(lines.length, processedMessage.length); i++) {
      let line = lines[i] || `Line ${i + 1} for encoding.`;
      
      if (i < processedMessage.length) {
        const targetChar = processedMessage[i];
        const words = line.split(' ');
        if (words.length > 0) {
          // Simple word replacement
          const firstChar = targetChar.toLowerCase();
          if (firstChar === 'a') words[0] = 'Amazing';
          else if (firstChar === 'b') words[0] = 'Beautiful';
          else if (firstChar === 'c') words[0] = 'Creative';
          else if (firstChar === 'd') words[0] = 'Delightful';
          else if (firstChar === 'e') words[0] = 'Excellent';
          else if (firstChar === 'f') words[0] = 'Fantastic';
          else if (firstChar === 'g') words[0] = 'Great';
          else if (firstChar === 'h') words[0] = 'Happy';
          else if (firstChar === 'i') words[0] = 'Incredible';
          else if (firstChar === 'j') words[0] = 'Joyful';
          else if (firstChar === 'k') words[0] = 'Kind';
          else if (firstChar === 'l') words[0] = 'Lovely';
          else if (firstChar === 'm') words[0] = 'Magnificent';
          else if (firstChar === 'n') words[0] = 'Nice';
          else if (firstChar === 'o') words[0] = 'Outstanding';
          else if (firstChar === 'p') words[0] = 'Perfect';
          else if (firstChar === 'q') words[0] = 'Quiet';
          else if (firstChar === 'r') words[0] = 'Remarkable';
          else if (firstChar === 's') words[0] = 'Special';
          else if (firstChar === 't') words[0] = 'Terrific';
          else if (firstChar === 'u') words[0] = 'Unique';
          else if (firstChar === 'v') words[0] = 'Vibrant';
          else if (firstChar === 'w') words[0] = 'Wonderful';
          else if (firstChar === 'x') words[0] = 'eXciting';
          else if (firstChar === 'y') words[0] = 'Youthful';
          else if (firstChar === 'z') words[0] = 'Zealous';
          
          line = words.join(' ');
        }
      }
      
      result.push(line);
    }
    
    return result.join('\n');
  }, [useEncryption, encryptText, password]);

  const extractFromAcrostic = useCallback((text: string): string => {
    const lines = text.split('\n');
    let extracted = '';
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.length > 0) {
        extracted += trimmed[0].toUpperCase();
      }
    }
    
    return useEncryption ? decryptText(extracted, password) : extracted;
  }, [useEncryption, decryptText, password]);

  // Process steganography operation
  const processOperation = useCallback(async () => {
    setIsProcessing(true);
    try {
      let result = '';

      if (operation === 'hide') {
        if (!coverText.trim()) {
          throw new Error('Please provide cover text');
        }
        if (!secretMessage.trim()) {
          throw new Error('Please provide secret message');
        }

        switch (method) {
          case 'whitespace':
            result = hideInWhitespace(coverText, secretMessage);
            break;
          case 'invisible':
            result = hideInInvisible(coverText, secretMessage);
            break;
          case 'unicode':
            result = hideInUnicode(coverText, secretMessage);
            break;
          case 'acrostic':
            result = hideInAcrostic(coverText, secretMessage);
            break;
          default:
            throw new Error('Invalid method');
        }

        setSteganographicText(result);
      } else {
        if (!steganographicText.trim()) {
          throw new Error('Please provide steganographic text');
        }

        switch (method) {
          case 'whitespace':
            result = extractFromWhitespace(steganographicText);
            break;
          case 'invisible':
            result = extractFromInvisible(steganographicText);
            break;
          case 'unicode':
            result = extractFromUnicode(steganographicText);
            break;
          case 'acrostic':
            result = extractFromAcrostic(steganographicText);
            break;
          default:
            throw new Error('Invalid method');
        }

        setExtractedMessage(result);
      }

      updateAnalytics(true);
    } catch (error) {
      updateAnalytics(false);
      alert(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  }, [operation, method, coverText, secretMessage, steganographicText,
      hideInWhitespace, hideInInvisible, hideInUnicode, hideInAcrostic,
      extractFromWhitespace, extractFromInvisible, extractFromUnicode, extractFromAcrostic]);

  // Update analytics
  const updateAnalytics = useCallback((success: boolean) => {
    const newHistoryItem: SteganographyHistory = {
      id: Date.now().toString(),
      operation,
      method,
      timestamp: new Date(),
      success
    };

    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);

    if (success) {
      setStats(prevStats => {
        const newMethodsUsed = { ...prevStats.methodsUsed };
        newMethodsUsed[method] = (newMethodsUsed[method] || 0) + 1;
        
        const messageLength = operation === 'hide' ? secretMessage.length : extractedMessage.length;
        const totalHidden = prevStats.totalCharactersHidden + (operation === 'hide' ? messageLength : 0);
        const totalMessagesHidden = prevStats.messagesHidden + (operation === 'hide' ? 1 : 0);
        
        return {
          ...prevStats,
          totalOperations: prevStats.totalOperations + 1,
          messagesHidden: totalMessagesHidden,
          messagesExtracted: prevStats.messagesExtracted + (operation === 'extract' ? 1 : 0),
          totalCharactersHidden: totalHidden,
          averageMessageLength: totalMessagesHidden > 0 ? Math.round(totalHidden / totalMessagesHidden) : 0,
          methodsUsed: newMethodsUsed
        };
      });
    }
  }, [operation, method, secretMessage, extractedMessage]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetAll = () => {
    setCoverText('');
    setSecretMessage('');
    setSteganographicText('');
    setExtractedMessage('');
    setPassword('');
  };

  const generateSampleText = () => {
    const samples = [
      "Lorem ipsum dolor sit amet consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam quis nostrud exercitation ullamco laboris.",
      "The quick brown fox jumps over the lazy dog. This sentence contains every letter of the alphabet and is perfect for testing steganographic methods.",
      "Steganography is the practice of concealing information within another message or physical object. It differs from cryptography which scrambles data to make it unreadable."
    ];
    if (operation === 'hide') {
      setCoverText(samples[Math.floor(Math.random() * samples.length)]);
    } else {
      setSteganographicText(samples[Math.floor(Math.random() * samples.length)]);
    }
  };

  const generateSampleMessage = () => {
    const messages = [
      "Secret message",
      "Hidden data",
      "TOP SECRET",
      "Hello World",
      "Test message"
    ];
    setSecretMessage(messages[Math.floor(Math.random() * messages.length)]);
  };

  // Visualize invisible characters
  const visualizeText = (text: string): string => {
    if (!showInvisibleChars) return text;
    return text
      .replace(/\u200B/g, '[ZWSP]')
      .replace(/\u200C/g, '[ZWNJ]')
      .replace(/\u200D/g, '[ZWJ]');
  };

  return (
    <>
      <Helmet>
        <title>Text Steganography - Hide Secret Messages in Text | Free Online Tool</title>
        <meta name="description" content="Hide and extract secret messages in text using whitespace, invisible characters, Unicode substitution, and acrostic methods." />
        <meta name="keywords" content="text steganography, hide message in text, invisible characters, whitespace steganography, unicode steganography" />
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FileText className="text-indigo-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Text Steganography</h1>
                <p className="text-sm opacity-60">Hide & Extract Secret Messages in Text</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowInvisibleChars(!showInvisibleChars)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Invisible Characters"
              >
                {showInvisibleChars ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help"
              >
                <Info size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Settings"
              >
                <Settings size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'hide' | 'extract')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="hide">🔒 Hide Message</option>
                    <option value="extract">🔍 Extract Message</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Method</label>
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value as SteganographyMethod)}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="whitespace">Whitespace</option>
                    <option value="invisible">Invisible Chars</option>
                    <option value="unicode">Unicode</option>
                    <option value="acrostic">Acrostic</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Optional..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={processOperation}
                    disabled={isProcessing}
                    className={`w-full p-2 rounded flex items-center justify-center space-x-2 ${
                      isProcessing
                        ? 'bg-gray-400 cursor-not-allowed'
                        : isDarkMode 
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                        : 'bg-indigo-500 hover:bg-indigo-600 text-white'
                    } transition-colors`}
                  >
                    {isProcessing ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    ) : operation === 'hide' ? (
                      <>
                        <Shield size={16} />
                        <span>Hide</span>
                      </>
                    ) : (
                      <>
                        <Eye size={16} />
                        <span>Extract</span>
                      </>
                    )}
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>

              {/* Method Description */}
              <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-sm">
                  <strong>{method.charAt(0).toUpperCase() + method.slice(1)} Method:</strong>
                  <span className="ml-2 opacity-75">
                    {method === 'whitespace' && 'Uses single/double spaces between words to encode binary data'}
                    {method === 'invisible' && 'Inserts invisible Unicode characters to hide information'}
                    {method === 'unicode' && 'Replaces characters with visually similar Unicode alternatives'}
                    {method === 'acrostic' && 'Hides message in first letters of lines or words'}
                  </span>
                </div>
              </div>
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Area */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {operation === 'hide' ? 'Cover Text' : 'Steganographic Text'}
                  </h3>
                  <button
                    onClick={generateSampleText}
                    className={`px-3 py-1 text-xs rounded ${
                      isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                    } text-white transition-colors`}
                  >
                    Sample
                  </button>
                </div>
                <textarea
                  value={operation === 'hide' ? coverText : steganographicText}
                  onChange={(e) => operation === 'hide' ? setCoverText(e.target.value) : setSteganographicText(e.target.value)}
                  placeholder={operation === 'hide' ? 
                    "Enter cover text where the secret message will be hidden..." : 
                    "Paste steganographic text to extract hidden message..."
                  }
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex justify-between items-center">
                  <span className="text-xs opacity-60">
                    {operation === 'hide' ? coverText.length : steganographicText.length} characters
                  </span>
                  <button
                    onClick={() => copyToClipboard(operation === 'hide' ? coverText : steganographicText)}
                    className={`px-2 py-1 text-xs rounded ${
                      isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'
                    } text-white transition-colors`}
                  >
                    <Copy size={12} className="inline mr-1" />
                    Copy
                  </button>
                </div>
              </div>

              {/* Message Area */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {operation === 'hide' ? 'Secret Message' : 'Extracted Message'}
                  </h3>
                  {operation === 'hide' && (
                    <button
                      onClick={generateSampleMessage}
                      className={`px-3 py-1 text-xs rounded ${
                        isDarkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'
                      } text-white transition-colors`}
                    >
                      Sample
                    </button>
                  )}
                </div>
                <textarea
                  value={operation === 'hide' ? secretMessage : extractedMessage}
                  onChange={(e) => operation === 'hide' && setSecretMessage(e.target.value)}
                  placeholder={operation === 'hide' ? 
                    "Enter your secret message..." : 
                    "Extracted message will appear here..."
                  }
                  readOnly={operation === 'extract'}
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex justify-between items-center">
                  <span className="text-xs opacity-60">
                    {operation === 'hide' ? secretMessage.length : extractedMessage.length} characters
                  </span>
                  {((operation === 'hide' && secretMessage) || (operation === 'extract' && extractedMessage)) && (
                    <button
                      onClick={() => copyToClipboard(operation === 'hide' ? secretMessage : extractedMessage)}
                      className={`px-2 py-1 text-xs rounded ${
                        isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'
                      } text-white transition-colors`}
                    >
                      <Copy size={12} className="inline mr-1" />
                      Copy
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Output Display */}
            {operation === 'hide' && steganographicText && (
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Steganographic Text (Result)</h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => downloadText(steganographicText, 'steganographic-text.txt')}
                      className={`px-3 py-1 text-xs rounded ${
                        isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                      } text-white transition-colors`}
                    >
                      <Download size={12} className="inline mr-1" />
                      Download
                    </button>
                    <button
                      onClick={() => copyToClipboard(steganographicText)}
                      className={`px-3 py-1 text-xs rounded ${
                        isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'
                      } text-white transition-colors`}
                    >
                      <Copy size={12} className="inline mr-1" />
                      Copy
                    </button>
                  </div>
                </div>
                <div className={`p-4 rounded border font-mono text-sm whitespace-pre-wrap ${
                  isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                }`}>
                  {visualizeText(steganographicText)}
                </div>
                <div className="mt-2 text-xs opacity-60">
                  {steganographicText.length} characters • Hidden using {method} method
                  {showInvisibleChars && method === 'invisible' && ' • Invisible characters shown as [ZWSP], [ZWNJ]'}
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Text Steganography Guide</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is Text Steganography?</h3>
                  <p>Text steganography hides secret messages within normal-looking text. Unlike encryption, which scrambles data, steganography conceals the existence of the message entirely.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Available Methods</h3>
                  <ul className="space-y-2">
                    <li>
                      <strong>Whitespace:</strong> Uses single/double spaces between words to encode binary data. Simple and robust.
                    </li>
                    <li>
                      <strong>Invisible Characters:</strong> Inserts zero-width Unicode characters that are invisible to the eye.
                    </li>
                    <li>
                      <strong>Unicode Substitution:</strong> Replaces characters with visually identical ones from other alphabets.
                    </li>
                    <li>
                      <strong>Acrostic:</strong> Hides the message in the first letters of lines or words.
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How to Use</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Choose your steganography method</li>
                    <li>For hiding: Enter cover text and secret message</li>
                    <li>For extracting: Paste steganographic text</li>
                    <li>Optionally set a password for encryption</li>
                    <li>Click Hide or Extract</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Tips</h3>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Use longer cover text for better hiding capacity</li>
                    <li>Test extraction before sharing steganographic text</li>
                    <li>Password encryption adds extra security</li>
                    <li>Different methods work better for different text types</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Note</h3>
                  <p className="text-orange-600">⚠️ This tool is for educational purposes. For sensitive data, use professional encryption methods.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Settings</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Use Encryption</label>
                  <input
                    type="checkbox"
                    checked={useEncryption}
                    onChange={(e) => setUseEncryption(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Invisible Characters</label>
                  <input
                    type="checkbox"
                    checked={showInvisibleChars}
                    onChange={(e) => setShowInvisibleChars(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Quick Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setMethod('invisible');
                        setUseEncryption(true);
                        setShowInvisibleChars(false);
                      }}
                      className={`w-full p-2 text-sm rounded ${
                        isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'
                      } text-white transition-colors`}
                    >
                      High Security
                    </button>
                    
                    <button
                      onClick={() => {
                        setMethod('whitespace');
                        setUseEncryption(false);
                        setShowInvisibleChars(true);
                      }}
                      className={`w-full p-2 text-sm rounded ${
                        isDarkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
                      } text-white transition-colors`}
                    >
                      Simple & Robust
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.messagesHidden}</div>
                  <div className="text-sm">Messages Hidden</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.messagesExtracted}</div>
                  <div className="text-sm">Messages Extracted</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.totalOperations}</div>
                  <div className="text-sm">Total Operations</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.averageMessageLength}</div>
                  <div className="text-sm">Avg Message Length</div>
                </div>
              </div>
              
              {Object.keys(stats.methodsUsed).length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-3">Method Usage</h3>
                  {Object.entries(stats.methodsUsed).map(([method, count]) => (
                    <div key={method} className="flex justify-between items-center mb-2">
                      <span className="capitalize">{method}</span>
                      <span>{count}</span>
                    </div>
                  ))}
                </div>
              )}
              
              <div>
                <h3 className="font-semibold mb-3">Recent Operations</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 10).map(item => (
                    <div key={item.id} className={`p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex justify-between items-center">
                        <div className="flex space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'hide' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.method.toUpperCase()}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.success ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                          }`}>
                            {item.success ? 'SUCCESS' : 'FAILED'}
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-4 opacity-60">
                      <FileText size={24} className="mx-auto mb-2" />
                      <div>No operations yet</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TextSteganographyApp;